import type { AxiosInstance } from 'axios';

export const buildBackofficeOrderStreamUrl = (
  baseUrl: string,
  accessToken: string,
  locationId: string,
  startISO?: string,
  endISO?: string,
) => {
  const root = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const path = `${root}/bp_backoffice_orders_stream`;
  const params = [
    `token=${encodeURIComponent(accessToken)}`,
    `location_id=${encodeURIComponent(locationId)}`,
  ];
  if (startISO) params.push(`bp_start_date=${encodeURIComponent(startISO)}`);
  if (endISO) params.push(`bp_end_date=${encodeURIComponent(endISO)}`);
  return `${path}?${params.join('&')}`;
};

// Omitting both dates makes the backend span epoch → now (see
// buildOrderUpdatedAtRange server-side), which is what the unpaid deep link needs.
const buildDateRangeQuery = (startISO?: string, endISO?: string) => {
  if (!startISO || !endISO) return '';

  return `&bp_start_date=${encodeURIComponent(
    startISO,
  )}&bp_end_date=${encodeURIComponent(endISO)}`;
};

// Core API functions (no app logic)
const getCompletedOrdersCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_owner_get_completed_orders_information?page=${page}&limit=${limit}&bp_start_date=${encodeURIComponent(
      startISO,
    )}&bp_end_date=${encodeURIComponent(endISO)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getPendingOrdersCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string | undefined,
  endISO: string | undefined,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_owner_get_pending_orders_information?page=${page}&limit=${limit}${buildDateRangeQuery(
      startISO,
      endISO,
    )}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getPartialOrdersCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string | undefined,
  endISO: string | undefined,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_owner_get_partially_paid_orders_information?page=${page}&limit=${limit}${buildDateRangeQuery(
      startISO,
      endISO,
    )}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getRecentOrdersCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_owner_get_recent_orders_information?page=${page}&limit=${limit}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const searchOrdersByDateCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  startISO: string,
  endISO: string,
  status: 'completed' | 'pending' | 'partial',
  page: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_owner_search_orders_information?bp_start_date=${encodeURIComponent(
      startISO,
    )}&bp_end_date=${encodeURIComponent(endISO)}&order_status=${status}&page=${page}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Search by receipt id for a specific owner (paginated)
const searchOrderByReceiptIdCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  searchText: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_owner_search_order_by_receipt_id_information?page=${page}&search_term=${encodeURIComponent(
      searchText,
    )}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Update individual order items (edit quantities/remove items)
const updateOrderItemsCore = async (
  axiosinstance: AxiosInstance,
  orderId: string,
  payload: {
    bp_order_items_information: Array<{
      bp_product_id: string;
      bp_variant_id?: string;
      bp_order_price: number;
      bp_order_quantity?: number | null;
      bp_order_mass?: number | null;
    }>;
  },
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.patch(
    `/bp_update_selected_order_product_information`,
    JSON.stringify({ ...payload, bp_order_id: orderId }),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// New: Web-parity single-item edit/remove endpoint
const removeOrEditOrderItemCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: {
    bp_order_id: string;
    bp_product_id: string;
    bp_variant_id?: string;
    bp_new_quantity?: number | null;
    bp_new_mass?: number | null;
    bp_product_price_type: string;
  },
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(
    `/bp_owner_remove_order_item_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Helper for single item edit/remove (mirrors web removeItemfromOrder by sending one item)
export const updateSingleOrderItem = async (
  axiosinstance: AxiosInstance,
  orderId: string,
  item: {
    bp_product_id: string;
    bp_variant_id?: string;
    bp_order_price: number;
    bp_order_quantity?: number | null;
    bp_order_mass?: number | null;
  },
  auth: { accessToken?: string | null },
) =>
  updateOrderItemsCore(
    axiosinstance,
    orderId,
    { bp_order_items_information: [item] },
    auth,
  );

// Exported: single-item edit/remove using web endpoint
export const removeOrEditOrderItem = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: {
    bp_order_id: string;
    bp_product_id: string;
    bp_variant_id?: string;
    bp_new_quantity?: number | null;
    bp_new_mass?: number | null;
    bp_product_price_type: string;
  },
  auth: { accessToken?: string | null },
) => removeOrEditOrderItemCore(axiosinstance, ownerId, payload, auth);

// Public wrappers
export const getCompletedOrders = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string,
  endISO: string,
  auth: { accessToken?: string | null },
) =>
  getCompletedOrdersCore(
    axiosinstance,
    ownerId,
    page,
    limit,
    startISO,
    endISO,
    auth,
  );

export const getPendingOrders = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string | undefined,
  endISO: string | undefined,
  auth: { accessToken?: string | null },
) =>
  getPendingOrdersCore(
    axiosinstance,
    ownerId,
    page,
    limit,
    startISO,
    endISO,
    auth,
  );

export const getPartialOrders = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startISO: string | undefined,
  endISO: string | undefined,
  auth: { accessToken?: string | null },
) =>
  getPartialOrdersCore(
    axiosinstance,
    ownerId,
    page,
    limit,
    startISO,
    endISO,
    auth,
  );

export const getRecentOrders = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => getRecentOrdersCore(axiosinstance, ownerId, page, limit, auth);

export const searchOrdersByDate = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  startISO: string,
  endISO: string,
  status: 'completed' | 'pending' | 'partial',
  page: number,
  auth: { accessToken?: string | null },
) =>
  searchOrdersByDateCore(
    axiosinstance,
    ownerId,
    startISO,
    endISO,
    status,
    page,
    auth,
  );

export const searchOrderByReceiptId = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  page: number,
  searchText: string,
  auth: { accessToken?: string | null },
) => searchOrderByReceiptIdCore(axiosinstance, ownerId, page, searchText, auth);

export const updateOrderItems = async (
  axiosinstance: AxiosInstance,
  orderId: string,
  payload: {
    bp_order_items_information: Array<{
      bp_product_id: string;
      bp_order_price: number;
      bp_order_quantity?: number | null;
      bp_order_mass?: number | null;
    }>;
  },
  auth: { accessToken?: string | null },
) => updateOrderItemsCore(axiosinstance, orderId, payload, auth);
